export type HornId = "velvet" | "flare" | "handstop" | "chorale";

export type HornPreset = {
  id: HornId;
  name: string;
  blurb: string;
  homeEffort: number;
  homeMute: number;
  threshold: number;
  effortCurve: number;
  attackSoftMs: number;
  attackHardMs: number;
  drive: number;
  asym: number;
  rasp: number;
  flow: number;
  direct: number;
  bore: number;
  flare: number;
  loopDark: number;
  loopOpen: number;
  muteDark: number;
  loss: number;
  delayTrim: number;
  noise: number;
  chiff: number;
  outGain: number;
  nasalAmt: number;
  nasalHz: number;
  nasalQ: number;
  sharpenCents: number;
  muteCents: number;
  scoopSoft: number;
  scoopHard: number;
  releaseSoftMs: number;
  releaseHardMs: number;
  closeMs: number;
  hardClose: number;
  tongueMs: number;
  slurShort: number;
  slurLong: number;
  releaseCents: number;
  body: number;
  voice2: number;
  detune: number;
  room: number;
  vibRate: number;
  vibCents: number;
  vibDelay: number;
  zip: number;
  talk: number;
  brightKnee: number;
  /**
   * Lip-only bore identity. The placed voice never reads these.
   * shadeKind: 0 velvet lip weight, 1 flare cuivre, 2 hand depth, 3 chorale beat.
   */
  shadeKind: number;
  cone: number;
  stop: number;
  lipMass: number;
  lipDamp: number;
  cuivre: number;
  lipGain: number;
};

/** Home position of each bore's own control. 0.62 is that bore's new lip sound. */
export const SHADE_HOME = 0.62;

export const SHADE_LABEL: Record<HornId, string> = {
  velvet: "Lip weight",
  flare: "Cuivre",
  handstop: "Hand depth",
  chorale: "Bore beat",
};

/** What the shade slider is doing on this bore. Home (0.62) is the sound above. */
export const SHADE_HINT: Record<HornId, string> = {
  velvet:
    "Lip weight. Heavier to the right: slower to speak, softer bell. Lighter to the left. Home is this bore's lip.",
  flare: "Cuivre. How far the open bell folds when it is pushed. Home is this bore's brass.",
  handstop: "Hand depth. How far the hand sits in the bell. Home is this stopped horn.",
  chorale: "Bore beat. The second bore's share of the chord. Home is the two bores as they are.",
};

const velvet: HornPreset = {
  id: "velvet",
  name: "Velvet",
  blurb: "Wide cylindrical bore, a heavy lip. Stays round until you lean in.",
  homeEffort: 0.36,
  homeMute: 0.1,
  threshold: 0.07,
  effortCurve: 1.15,
  attackSoftMs: 240,
  attackHardMs: 36,
  drive: 5.2,
  asym: 0.4,
  rasp: 0.16,
  flow: 0.22,
  direct: 0.32,
  bore: 0.32,
  flare: 0.42,
  loopDark: 0.28,
  loopOpen: 0.18,
  muteDark: 0.5,
  loss: 0.96,
  delayTrim: 1.6,
  noise: 0.05,
  chiff: 0.07,
  outGain: 0.6,
  nasalAmt: 0.8,
  nasalHz: 1420,
  nasalQ: 3.2,
  sharpenCents: 11,
  muteCents: 8,
  scoopSoft: 48,
  scoopHard: 10,
  releaseSoftMs: 520,
  releaseHardMs: 90,
  closeMs: 26,
  hardClose: 0.82,
  tongueMs: 28,
  slurShort: 55,
  slurLong: 130,
  releaseCents: 22,
  body: 0.26,
  voice2: 0,
  detune: 8,
  room: 0.18,
  vibRate: 5.05,
  vibCents: 8,
  vibDelay: 0.72,
  zip: 0.5,
  talk: 0.035,
  brightKnee: 0.4,
  shadeKind: 0,
  cone: 0,
  stop: 0,
  lipMass: 1.48,
  lipDamp: 0.72,
  cuivre: 0,
  lipGain: 1.11,
};

export const PRESETS: Record<HornId, HornPreset> = {
  velvet,
  flare: {
    ...velvet,
    id: "flare",
    name: "Flare",
    blurb: "Conical bore, a light lip. The bell goes brassy when you push.",
    homeEffort: 0.5,
    homeMute: 0.05,
    threshold: 0.05,
    effortCurve: 0.92,
    attackSoftMs: 90,
    attackHardMs: 16,
    drive: 9.5,
    asym: 0.72,
    rasp: 0.85,
    flow: 0.18,
    direct: 0.42,
    bore: 0.16,
    flare: 0.82,
    loopDark: 0.12,
    loopOpen: 0.1,
    muteDark: 0.4,
    loss: 0.93,
    delayTrim: 0.8,
    noise: 0.028,
    chiff: 0.16,
    outGain: 0.5,
    nasalAmt: 0.55,
    nasalHz: 2200,
    nasalQ: 2.4,
    sharpenCents: 16,
    muteCents: 7,
    scoopSoft: 18,
    scoopHard: 4,
    releaseSoftMs: 200,
    releaseHardMs: 48,
    closeMs: 14,
    hardClose: 0.64,
    tongueMs: 14,
    slurShort: 30,
    slurLong: 80,
    releaseCents: 12,
    body: 0.08,
    room: 0.1,
    vibRate: 5.6,
    vibCents: 5,
    vibDelay: 0.9,
    zip: 0.9,
    talk: 0.06,
    brightKnee: 0.08,
    shadeKind: 1,
    cone: 0.34,
    stop: 0,
    lipMass: 0.92,
    lipDamp: 0,
    cuivre: 0.82,
    lipGain: 1,
  },
  handstop: {
    ...velvet,
    id: "handstop",
    name: "Handstop",
    blurb: "Hand in the bell. The bore closes and a cavity speaks.",
    homeEffort: 0.52,
    homeMute: 0.82,
    threshold: 0.06,
    effortCurve: 1.05,
    attackSoftMs: 140,
    attackHardMs: 24,
    drive: 7.2,
    asym: 0.58,
    rasp: 0.62,
    flow: 0.2,
    direct: 0.28,
    bore: 0.2,
    flare: 0.28,
    loopDark: 0.4,
    loopOpen: 0.1,
    muteDark: 0.22,
    loss: 0.94,
    delayTrim: 2.2,
    noise: 0.035,
    chiff: 0.09,
    outGain: 0.56,
    nasalAmt: 1.45,
    nasalHz: 1720,
    nasalQ: 4.6,
    sharpenCents: 8,
    muteCents: 16,
    scoopSoft: 24,
    scoopHard: 6,
    releaseSoftMs: 260,
    releaseHardMs: 60,
    closeMs: 16,
    hardClose: 0.7,
    tongueMs: 18,
    slurShort: 40,
    slurLong: 100,
    releaseCents: 14,
    body: 0.12,
    room: 0.12,
    vibCents: 5,
    vibDelay: 0.95,
    zip: 0.35,
    talk: 0.05,
    brightKnee: 0.28,
    shadeKind: 2,
    cone: 0,
    stop: 0.78,
    lipMass: 1.18,
    lipDamp: 1.2,
    cuivre: 0,
    lipGain: 0.862,
  },
  chorale: {
    ...velvet,
    id: "chorale",
    name: "Chorale",
    blurb: "Two bores, a slow beat, and a long ring after you let go.",
    homeEffort: 0.32,
    homeMute: 0.18,
    threshold: 0.08,
    effortCurve: 1.25,
    attackSoftMs: 320,
    attackHardMs: 70,
    drive: 3.4,
    asym: 0.22,
    rasp: 0.05,
    flow: 0.2,
    direct: 0.26,
    bore: 0.36,
    flare: 0.3,
    loopDark: 0.34,
    loopOpen: 0.14,
    noise: 0.055,
    chiff: 0.04,
    outGain: 0.46,
    scoopSoft: 34,
    scoopHard: 12,
    releaseSoftMs: 680,
    releaseHardMs: 180,
    closeMs: 36,
    hardClose: 0.92,
    tongueMs: 40,
    slurShort: 70,
    slurLong: 160,
    releaseCents: 24,
    body: 0.3,
    voice2: 0.78,
    detune: 8,
    room: 0.24,
    vibRate: 4.6,
    vibCents: 9,
    vibDelay: 0.95,
    zip: 0.3,
    talk: 0.02,
    brightKnee: 0.46,
    shadeKind: 3,
    cone: 0,
    stop: 0,
    lipMass: 1,
    lipDamp: 0,
    cuivre: 0,
    lipGain: 1,
  },
};

export const PRESET_ORDER: HornId[] = ["velvet", "flare", "handstop", "chorale"];
